import { FC, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateUser,
  setSavedSignature,
  setSavedInitials,
  setSessions,
  setLoginSecurity,
  setElevatedAuth,
  setElevatedAuthTotpEnabled,
  selectAccountHubUserId,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import type {
  ActiveSession,
  LoginSecurityState,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import {
  updateHouseholdMemberInCertifications,
  updateCertMemberContactsByUserId,
  setProfileHouseholdCompositionConfirmed,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import UserWebSocket from 'src/components/NewRenterInterface/UserWebSocket';
import elevatedAuthService from 'src/services/elevatedAuth.service';
import {
  applyGoogleTranslateLanguage,
  getGoogleTranslateCookie,
} from 'src/hooks/useGoogleTranslate';
import {
  isAccountHubEventForUser,
  isFullContactSnapshot,
} from 'src/components/NewRenterInterface/AccountHub/accountHubUtils';
import { removeTokensMetadata } from 'src/utils/jwt';
import { CertMember } from 'src/types/Certification';

interface UserProfilePayload {
  user: Record<string, unknown>;
}

interface SavedSignaturePayload {
  signature_data?: string | null;
}

interface SavedInitialsPayload {
  initials_text?: string | null;
  font_family?: string | null;
  signature_data?: string | null;
}

interface WebSocketEventDetail {
  data: unknown;
  type: string;
  source?: string;
}

function isUserProfilePayload(data: unknown): data is UserProfilePayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'user' in data &&
    typeof (data as { user: unknown }).user === 'object'
  );
}

function isSavedSignaturePayload(data: unknown): data is SavedSignaturePayload {
  return typeof data === 'object' && data !== null;
}

function isSavedInitialsPayload(data: unknown): data is SavedInitialsPayload {
  return typeof data === 'object' && data !== null;
}

function isValidWebSocketEvent(
  event: CustomEvent
): event is CustomEvent<WebSocketEventDetail> {
  return (
    event.detail &&
    typeof event.detail === 'object' &&
    'type' in event.detail &&
    typeof event.detail.type === 'string' &&
    'data' in event.detail
  );
}

/** Mirror of mapWsPayloadToCertMember in CertificationUpdateManager. */
function mapWsPayloadToCertMember(data: Record<string, unknown>): CertMember {
  return {
    cert_member_id: data.id as number,
    user_id: (data.user_id as number) ?? null,
    user_household_member_id: data.user_household_member_id as number,
    is_head_of_household: data.is_head_of_household as boolean,
    is_helper: data.is_helper as boolean,
    member_type_id: data.member_type_id as number,
    member_type_name: (data.member_type_name as string) || '',
    member_type_weight: (data.member_type_weight as number) ?? null,
    first_name: (data.first_name as string) || '',
    middle_name: (data.middle_name as string) ?? null,
    last_name: (data.last_name as string) || '',
    emails: ((data.emails as Array<Record<string, unknown>>) || []).map(
      (e) => ({
        id: e.id as number,
        email: e.email as string,
        is_primary: e.is_primary as boolean,
      })
    ),
    phones: ((data.phones as Array<Record<string, unknown>>) || []).map(
      (p) => ({
        id: p.id as number,
        phone_number: p.phone_number as string,
        is_primary: p.is_primary as boolean,
      })
    ),
    surveys: (data.surveys as number[]) || [],
  };
}

const UserUpdateManager: FC = () => {
  const dispatch = useDispatch();
  const selectedUserId = useSelector(selectAccountHubUserId);

  const handleUserProfileUpdated = useCallback(
    (data: UserProfilePayload): void => {
      if (!data?.user) {
        return;
      }
      dispatch(updateUser(data.user));

      // If the language_preference changed, apply the Google Translate cookie
      // and reload so the page renders in the new language.
      const newLang = (data.user as Record<string, unknown>)
        .language_preference as string | undefined;
      if (newLang !== undefined) {
        const currentCookieLang = getGoogleTranslateCookie();
        // Only reload if the language actually changed
        if (newLang !== currentCookieLang) {
          // Clear the 3-second fallback timer from useGoogleTranslate by
          // applying immediately here — the WS event arrived within the window.
          applyGoogleTranslateLanguage(newLang);
        }
      }
    },
    [dispatch]
  );

  const handleESignSavedSignatureChanged = useCallback(
    (data: { signature_data?: string | null }): void => {
      dispatch(setSavedSignature(data.signature_data ?? null));
      const event = new CustomEvent('SavedSignatureChanged', {
        detail: { signatureData: data.signature_data },
      });
      document.dispatchEvent(event);
    },
    [dispatch]
  );

  const handleESignSavedInitialsChanged = useCallback(
    (data: {
      initials_text?: string | null;
      font_family?: string | null;
      signature_data?: string | null;
    }): void => {
      if (data.signature_data) {
        dispatch(setSavedInitials({ xfdf: data.signature_data }));
      } else {
        dispatch(
          setSavedInitials({
            text: data.initials_text ?? null,
            font: data.font_family ?? null,
          })
        );
      }
      const event = new CustomEvent('SavedInitialsChanged', {
        detail: {
          initialsText: data.initials_text,
          fontFamily: data.font_family,
          signatureData: data.signature_data,
        },
      });
      document.dispatchEvent(event);
    },
    [dispatch]
  );

  const onWebSocketEvent = useCallback(
    (event: CustomEvent): void => {
      if (!isValidWebSocketEvent(event)) {
        return;
      }

      const { data, type, source } = event.detail;

      // Validate event source to prevent fake events
      if (source && source !== 'pronto-websocket') {
        console.warn('Ignoring non-WebSocket event');
        return;
      }

      switch (type) {
        case 'user_profile_updated':
          if (isUserProfilePayload(data)) {
            handleUserProfileUpdated(data);
          }
          break;
        case 'esign_saved_signature_changed':
          if (isSavedSignaturePayload(data)) {
            handleESignSavedSignatureChanged(data);
          }
          break;
        case 'esign_saved_initials_changed':
          if (isSavedInitialsPayload(data)) {
            handleESignSavedInitialsChanged(data);
          }
          break;
        case 'sessions_updated':
          if (
            isAccountHubEventForUser(data, selectedUserId) &&
            data !== null &&
            typeof data === 'object' &&
            'sessions' in (data as Record<string, unknown>) &&
            Array.isArray((data as Record<string, unknown>)['sessions'])
          ) {
            dispatch(
              setSessions((data as { sessions: ActiveSession[] }).sessions)
            );
          }
          break;
        case 'login_security_updated':
          if (
            isAccountHubEventForUser(data, selectedUserId) &&
            data !== null &&
            typeof data === 'object'
          ) {
            const payload = data as Partial<LoginSecurityState>;
            dispatch(setLoginSecurity(payload));
            // Keep elevatedAuth.totp_enabled in sync — AccountHubPage reads
            // from that slice to decide which gate overlay to show.
            if (typeof payload.totp_enabled === 'boolean') {
              dispatch(setElevatedAuthTotpEnabled(payload.totp_enabled));
            }
          }
          break;
        case 'session_revoked':
          // Attempt a token refresh. If this device's refresh token was the one
          // revoked, the call will fail and removeTokensMetadata will log it out.
          // All other devices will refresh successfully and remain active.
          axios
            .post('token/access/', null, {
              baseURL:
                import.meta.env.VITE_API_ROOT || 'http://localhost:8000/api/',
              withCredentials: true,
            })
            .catch(() => {
              removeTokensMetadata();
            });
          break;
        case 'user_household_composition_confirmed':
          // Profile (cert-less) household composition confirmed — clear the
          // unconfirmed state live so the forced modal dismisses everywhere.
          if (
            data !== null &&
            typeof data === 'object' &&
            'user_household_id' in (data as Record<string, unknown>)
          ) {
            const userHouseholdId = (data as Record<string, unknown>)
              .user_household_id;
            if (typeof userHouseholdId === 'number') {
              dispatch(
                setProfileHouseholdCompositionConfirmed({ userHouseholdId })
              );
            }
          }
          break;
        case 'contact_info_updated':
          if (isFullContactSnapshot(data)) {
            dispatch(
              updateCertMemberContactsByUserId({
                userId: data.affected_user_id,
                emails: data.emails as CertMember['emails'],
                phones: data.phones as CertMember['phones'],
              })
            );
          }
          break;
        case 'cert_member_added':
          if (
            data !== null &&
            typeof data === 'object' &&
            'certification_id' in (data as Record<string, unknown>)
          ) {
            const addedMember = mapWsPayloadToCertMember(
              data as Record<string, unknown>
            );
            dispatch(
              updateHouseholdMemberInCertifications({
                certificationId: (data as Record<string, unknown>)
                  .certification_id as number,
                householdMember: addedMember,
                action: 'add',
              })
            );
          }
          break;
        case 'cert_member_updated':
          if (
            data !== null &&
            typeof data === 'object' &&
            'certification_id' in (data as Record<string, unknown>)
          ) {
            const updatedMember = mapWsPayloadToCertMember(
              data as Record<string, unknown>
            );
            dispatch(
              updateHouseholdMemberInCertifications({
                certificationId: (data as Record<string, unknown>)
                  .certification_id as number,
                householdMember: updatedMember,
                action: 'update',
              })
            );
          }
          break;
        case 'cert_member_removed':
          if (
            data !== null &&
            typeof data === 'object' &&
            'certification_id' in (data as Record<string, unknown>) &&
            'certification_member_id' in (data as Record<string, unknown>)
          ) {
            const d = data as Record<string, unknown>;
            dispatch(
              updateHouseholdMemberInCertifications({
                certificationId: d.certification_id as number,
                householdMember: {
                  cert_member_id: d.certification_member_id as number,
                  user_id: null,
                  user_household_member_id: 0,
                  is_head_of_household: false,
                  is_helper: false,
                  member_type_id: 0,
                  member_type_name: '',
                  member_type_weight: null,
                  first_name: '',
                  middle_name: null,
                  last_name: '',
                  emails: [],
                  phones: [],
                  surveys: [],
                },
                action: 'remove',
              })
            );
          }
          break;
      }
    },
    [
      handleUserProfileUpdated,
      handleESignSavedSignatureChanged,
      handleESignSavedInitialsChanged,
      dispatch,
      selectedUserId,
    ]
  );

  useEffect(() => {
    document.addEventListener('UserWebSocketEvent', onWebSocketEvent);
    return () => {
      document.removeEventListener('UserWebSocketEvent', onWebSocketEvent);
    };
  }, [onWebSocketEvent]);

  // Prefetch the authenticator status once, app-wide, so surfaces that gate on
  // it (e.g. household member contact editing) know up front whether 2FA is
  // configured instead of flashing an enabled/disabled state while they fetch.
  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await elevatedAuthService.getStatus();
        if (cancelled) {
          return;
        }
        dispatch(
          setElevatedAuth({
            verified: res.verified,
            expires_at: res.expires_at ?? undefined,
            totp_enabled: res.totp_enabled,
            method: res.method ?? null,
          })
        );
      } catch {
        // Non-fatal — consumers fall back to their own status check.
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  return <UserWebSocket />;
};

export default UserUpdateManager;
